// @flow
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { featureIsEnabled } from '@ta/common.features';
import { legacyBuildNearbyUrl, legacyBuildUrl } from '@ta/brand.geopill-util';
import { useGeoLocation } from '@ta/common.geolocation';
import { RouteContext } from '@ta/common.routing';
import { Device } from '@ta/lithium.context';
import { useAttrPromoScreen } from '@ta/memx.attr-promo-screen';
import {
  DatePicker,
  GeoPicker,
  LoadingSpinner,
  MonthPicker,
  NavBar,
  NextButton,
  ProgressIndicator,
  StepsContainer,
  Switch,
  TagCloudPicker,
  TextInput,
} from '@ta/onboarding.common';
import { useLocalize } from '@ta/platform.i18n';
import { useGARecorder } from '@ta/platform.interactions';
import DestinationImages from './DestinationImages';
import EndScreen from './EndScreen';
import HasDestinationPicker from './HasDestinationPicker';
import Header from './Header';
import Modal from './Modal';
import SegmentImages from './SegmentImages';
import SegmentPicker from './SegmentPicker';
import {
  type ExplicitPreferencesMode,
  type ExplicitPreferencesState,
} from './useExplicitPreferencesState';
import useExplicitPreferencesInteraction from './useExplicitPreferencesInteraction';
import styles from './ExplicitPreferencesUI.css';

/**
 * How many pixels of scrolling is considered "scrolled" for applying the scrolled appbar styles
 */
const SCROLL_THRESHOLD = 12;

/**
 * @return a callback to prompt for location permissions and redirect to a nearby-scoped URL
 */
const useNearbyRedirect = () => {
  const [redirect, setRedirect] = useState(false);
  const { latitude, longitude, errorCode } = useGeoLocation({ pause: !redirect });

  useEffect(() => {
    if (redirect) {
      if (latitude != null && longitude != null) {
        // eslint-disable-next-line no-restricted-globals
        window.location = legacyBuildNearbyUrl(latitude, longitude);
      } else if (errorCode != null) {
        // eslint-disable-next-line no-restricted-globals
        window.location.reload();
      }
    }
  }, [redirect, latitude, longitude, errorCode]);

  return useCallback(() => setRedirect(true), []);
};

/**
 * @return a promise that resolves in the given number of milliseconds
 */
const createTimeoutPromise = (timeoutMs: number) =>
  new Promise(resolve => setTimeout(resolve, timeoutMs));

type Props = {|
  mode: ExplicitPreferencesMode,

  state: ExplicitPreferencesState,

  onClose: () => mixed,

  /**
   * Optional callback to refresh the feed after changes are submitted
   */
  onRefresh?: () => Promise<*>,
|};

/**
 * The Explicit Preferences UI
 *
 * This renders each step of the flow while it's open. {@link ./useExplicitPreferencesState} manages
 * the state, and {@link ./useExplicitPreferencesModal} connects these two and provides an
 * entrypoint to start the flow.
 */
const ExplicitPreferencesUI = ({
  mode,
  state: {
    steps,
    currentStepIndex,
    nextStep,
    progress,
    canGoBack,
    canGoForward,
    canSkip,
    goBack,
    goForward,
    reset,
    save,
    triggerEmail,
    hasExistingResponses,
    interestOptions,
    destinationGeoName,
    homeGeoName,
    modified,
    responses,
    setters,
  },
  onClose,
  onRefresh,
}: Props) => {
  const { route } = useContext(RouteContext);
  const { localize } = useLocalize();
  const redirectNearby = useNearbyRedirect();
  const [scrolled, setScrolled] = useState(false);
  const autoOpenGeoPicker = mode === 'GEOPILL';
  const [hideControls, setHideControls] = useState(autoOpenGeoPicker);
  const [saving, setSaving] = useState(false);
  const currentStep = steps[currentStepIndex];

  const displayName = responses.firstName.trim() || null;

  // If a destination geo was picked rescope the page to it (unless we're already on a detail page)
  const willRedirectToGeo =
    route.detail == null &&
    responses.hasDestination === true &&
    !responses.isInDestination &&
    responses.destinationGeoId != null &&
    responses.destinationGeoId !== +route.geo;

  // If nearby was chosen, scope to nearby (unless we're already on a detail page)
  const willRedirectToNearby =
    route.detail == null && responses.hasDestination === true && responses.isInDestination === true;

  // If we're going to redirect (which takes at least a few seconds) or this is the user's first
  // time through the flow, show a loading screen at the end
  const showLoadingScreen = !hasExistingResponses || willRedirectToGeo || willRedirectToNearby;

  // MEMX-1268 Saves Educator Screen for End Screen
  const { browser } = React.useContext(Device);
  const isEducatorEligible =
    (mode === 'ONBOARDING' || mode === 'ONETAP') && browser.family !== 'IE';
  const showSavesEducator =
    featureIsEnabled('saves_educator_explicit_preferences') && isEducatorEligible;

  // MEMX-1478 Trigger Promo Screen after explicit preferences flow
  const trackPromoScreen =
    featureIsEnabled('post_signup_attr_promo_explicit_preferences_control') ||
    featureIsEnabled('post_signup_attr_promo_explicit_preferences');
  const showPromoScreen = featureIsEnabled('post_signup_attr_promo_explicit_preferences');
  const openAttrPromo = useAttrPromoScreen();
  // todo - clean up GARecord usages
  const recordAction = useGARecorder();
  // MEMX-1003/1478 tracking
  const trackPromo = React.useCallback(() => {
    recordAction({
      module: 'signup_promo_prompt',
      action: 'eligible_promo_test_explicit_preferences',
    });
  }, [recordAction]);
  const handlePromoScreen = React.useCallback(() => {
    // MEMX-1478
    if (mode === 'ONETAP') {
      if (trackPromoScreen) {
        trackPromo();
      }
      if (showPromoScreen) {
        openAttrPromo();
      }
    }
  }, [showPromoScreen, openAttrPromo, trackPromoScreen, trackPromo, mode]);

  const {
    trackRespondQuestion,
    trackSkipQuestion,
    trackGoBackQuestion,
    trackCloseModal,
  } = useExplicitPreferencesInteraction(currentStep, responses, hasExistingResponses, mode);

  const close = useCallback(() => {
    setSaving(false);
    triggerEmail();
    reset();
    onClose();
    // eslint-disable-next-line no-restricted-globals
    window.location.hash = '#preferences';
    handlePromoScreen();
  }, [reset, triggerEmail, onClose, handlePromoScreen]);

  // Save the user's selections, then either close the modal or redirect to a new page
  const saveAndClose = useCallback(() => {
    if (saving) {
      return;
    }

    // If nothing changed, close the modal without saving anything or redirecting
    if (!modified) {
      close();
      return;
    }

    setSaving(true);

    const savePromises = [
      // Save the trip preferences
      save().then(res => {
        // If we're not going to reload the page, refresh the feed after preferences have been saved
        if (!willRedirectToGeo && !willRedirectToNearby && onRefresh) {
          return onRefresh();
        }

        return res;
      }),
    ];

    // If we're showing the loading screen but not actually reloading, make it take longer to save
    // (see TRVX-14610)
    if (showSavesEducator && showLoadingScreen) {
      savePromises.push(createTimeoutPromise(7000));
    } else if (showLoadingScreen && !willRedirectToGeo && !willRedirectToNearby) {
      savePromises.push(createTimeoutPromise(2000));
    }

    let saveCallback;
    if (willRedirectToGeo) {
      saveCallback = () => {
        triggerEmail();
        // Redirect to the #preferences fragment (in ExplicitPreferencesCta.jsx) so the
        // payoff is above the fold
        // eslint-disable-next-line no-restricted-globals
        window.location = `${legacyBuildUrl(String(responses.destinationGeoId))}#preferences`;
      };
    } else if (willRedirectToNearby) {
      // Redirect to the nearby scoped page if that was selected
      saveCallback = () => {
        triggerEmail();
        redirectNearby();
      };
    } else {
      // Otherwise, just close the modal
      saveCallback = close;
    }

    Promise.all(savePromises).then(saveCallback, close);
  }, [
    modified,
    close,
    onRefresh,
    redirectNearby,
    save,
    saving,
    willRedirectToGeo,
    willRedirectToNearby,
    showLoadingScreen,
    showSavesEducator,
    responses.destinationGeoId,
    triggerEmail,
  ]);

  const saveAndCloseWithLogging = useCallback(() => {
    trackCloseModal();
    saveAndClose();
  }, [saveAndClose, trackCloseModal]);

  const forward = useCallback(() => {
    if (nextStep === 'END_SCREEN' && !showLoadingScreen) {
      saveAndClose();
    } else {
      goForward();
    }
  }, [showLoadingScreen, nextStep, saveAndClose, goForward]);

  const forwardNext = useCallback(() => {
    trackRespondQuestion();
    forward();
  }, [forward, trackRespondQuestion]);

  const forwardSkip = useCallback(() => {
    trackSkipQuestion();
    forward();
  }, [forward, trackSkipQuestion]);

  const goBackWithLogging = useCallback(() => {
    trackGoBackQuestion();
    goBack();
  }, [trackGoBackQuestion, goBack]);

  const lastStep = useRef(currentStep);
  useEffect(() => {
    // When we get to the end screen, save the user's changes and exit the flow, landing onto the
    // scoped page if relevant.
    if (!saving && currentStep === 'END_SCREEN' && currentStep !== lastStep.current) {
      saveAndClose();
    }

    lastStep.current = currentStep;
  }, [saving, currentStep, saveAndClose]);

  const onScroll = useCallback((scrollPosition: number) => {
    setScrolled(scrollPosition >= SCROLL_THRESHOLD);
  }, []);

  return (
    // During onboarding, do not allow closing by clicking out of the modal
    <Modal onClose={mode === 'ONBOARDING' ? undefined : saveAndCloseWithLogging}>
      {/* Background images. These are always rendered, but only set to active on the appropriate
       steps, so we can animate them in and out with CSS transitions. */}
      <SegmentImages active={currentStep === 'SEGMENT'} />
      <DestinationImages active={currentStep === 'DESTINATION'} />

      {!hideControls && (
        <NavBar
          isFirstStep={currentStepIndex === 0}
          canGoBack={canGoBack}
          canSkip={canSkip}
          isScrolled={scrolled}
          onClose={saveAndCloseWithLogging}
          onBack={goBackWithLogging}
          onSkip={forwardSkip}
        />
      )}

      <StepsContainer steps={steps} currentStepIndex={currentStepIndex} onScroll={onScroll}>
        {({ step, scroll }) => {
          switch (step) {
            case 'HAS_DESTINATION':
              return (
                <HasDestinationPicker
                  displayName={displayName}
                  onSelect={setters.setHasDestination}
                />
              );

            case 'DESTINATION':
              return (
                <>
                  <Header
                    // Great. Where to?
                    titleText={localize('cx_explicit_preferences_destination_header2')}
                    // We'll show you recommendations from other travelers who've been there.
                    subtitleText={localize('cx_explicit_preferences_destination_subheader2')}
                  />

                  <div className={styles.destinationGeoContainer}>
                    <GeoPicker
                      placeholder={localize.string('dual_search_ghost_im_going_to')} // I'm going to...
                      showPopularDestinations
                      showNearby
                      geoName={destinationGeoName}
                      onChange={setters.setDestinationGeo}
                      onNearbySelected={setters.setIsInDestination}
                      onShowHide={setHideControls}
                      autoOpen={autoOpenGeoPicker}
                    >
                      {props => (
                        <TextInput
                          {...props}
                          type="OUTLINED"
                          placeholder={localize.string('dual_search_ghost_im_going_to')} // I'm going to...
                          label={localize.string('common_Destination_3dd')} // Destination
                          iconName="map-pin-fill"
                        />
                      )}
                    </GeoPicker>
                  </div>
                </>
              );

            case 'DATES':
              return (
                <>
                  <Header
                    // When are you traveling to {destinationGeoName}?
                    titleText={localize('cx_explicit_preferences_dates_header', {
                      destinationGeoName,
                    })}
                  />

                  <Switch
                    label={localize('cx_explicit_preferences_dates_flexible')} // My dates are flexible
                    value={responses.flexibleDates}
                    onChange={setters.setFlexibleDates}
                  />

                  {responses.flexibleDates ? (
                    <MonthPicker
                      startMonth={responses.startMonth}
                      endMonth={responses.endMonth}
                      onStartMonthChanged={setters.setStartMonth}
                      onEndMonthChanged={setters.setEndMonth}
                    />
                  ) : (
                    <DatePicker
                      scrollContainer={scroll}
                      startDate={responses.startDate}
                      endDate={responses.endDate}
                      onStartDateChanged={setters.setStartDate}
                      onEndDateChanged={setters.setEndDate}
                    />
                  )}
                </>
              );

            case 'SEGMENT':
              return (
                <>
                  {/* Who are you traveling with? */}
                  <Header titleText={localize('cx_explicit_preferences_segment_header')} />

                  <SegmentPicker value={responses.segment} onSelect={setters.setSegment} />
                </>
              );

            case 'INTERESTS':
              return (
                <>
                  <Header
                    // What do you want to do on your trip?
                    titleText={localize('cx_explicit_preferences_interests_header2')}
                    // Take a look at what {destinationGeoName} travelers are talking about and select what you might like.
                    subtitleText={localize('cx_explicit_preferences_interests_subheader3', {
                      destinationGeoName,
                    })}
                  />

                  <TagCloudPicker
                    options={interestOptions}
                    values={responses.interests}
                    onChange={setters.setInterests}
                  />
                </>
              );

            case 'PROFILE':
              return (
                <>
                  {/* Welcome! Tell us about yourself. */}
                  <Header titleText={localize('cx_explicit_preferences_profile_header3')} />

                  <div className={styles.profileContainer}>
                    <TextInput
                      type="UNDERLINED"
                      label={localize('cx_explicit_preferences_profile_name')} // My first name is...
                      maxLength={40}
                      value={responses.firstName}
                      onChange={setters.setFirstName}
                    />

                    <div className={styles.helperText}>
                      {/* This is how your name will appear on TripAdvisor */}
                      {localize('cx_explicit_preferences_profile_name_help')}
                    </div>

                    <GeoPicker
                      placeholder={localize.string('cx_explicit_preferences_profile_hometown')} // I live in...
                      closeOnSelection
                      geoName={homeGeoName}
                      onChange={setters.setHomeGeo}
                      onShowHide={setHideControls}
                    >
                      {props => (
                        <TextInput
                          {...props}
                          type="UNDERLINED"
                          label={localize('cx_explicit_preferences_profile_hometown')} // I live in...
                        />
                      )}
                    </GeoPicker>

                    <div className={styles.helperText}>
                      {/* Get travel ideas and flight deals based on where you live */}
                      {localize('cx_explicit_preferences_profile_hometown_help2')}
                    </div>
                  </div>
                </>
              );

            case 'END_SCREEN':
              return (
                <EndScreen
                  displayName={displayName}
                  destinationGeoName={destinationGeoName}
                  willRedirectToNearby={willRedirectToNearby}
                  isEducatorEligible={isEducatorEligible}
                />
              );

            default:
              throw new Error(`Invalid step: ${(step: empty)}`);
          }
        }}
      </StepsContainer>

      <NextButton
        iconName={nextStep === 'END_SCREEN' ? 'checkmark' : 'arrow-right'}
        disabled={!canGoForward || hideControls}
        onClick={forwardNext}
      />

      <ProgressIndicator
        active={progress != null}
        stepNumber={progress != null ? progress.stepNumber : 0}
        stepCount={progress != null ? progress.stepCount : 1}
      />

      {/* If we're saving by exiting the flow early, show a spinner overlay */}
      {saving && currentStep !== 'END_SCREEN' && (
        <div className={styles.savingOverlay}>
          <LoadingSpinner />
        </div>
      )}
    </Modal>
  );
};

export default ExplicitPreferencesUI;
